/**
 * Dark/light/system mode - toggles the `.dark` class on <html> (ported from
 * web_admin). The source of truth is the user's preference
 * (`UserPreference.theme`), loaded from the server on the Preferences tab;
 * localStorage keeps the choice for the frames before that lands.
 */
export type ThemeMode = "system" | "light" | "dark";
const STORAGE_KEY = "wa_account_theme";

export function readThemeMode(): ThemeMode {
  if (typeof window === "undefined") return "system";
  const v = window.localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
  return v === "light" || v === "dark" || v === "system" ? v : "system";
}

function resolveDark(mode: ThemeMode): boolean {
  if (mode === "dark") return true;
  if (mode === "light") return false;
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function applyThemeMode(mode: ThemeMode): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", resolveDark(mode));
  if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, mode);
}

/** Apply the stored mode at boot and follow system changes. Returns a cleanup. */
export function bindThemeModeEffects(): () => void {
  applyThemeMode(readThemeMode());
  if (typeof window === "undefined") return () => {};
  const mq = window.matchMedia("(prefers-color-scheme: dark)");
  const onChange = () => {
    if (readThemeMode() === "system") applyThemeMode("system");
  };
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}
